// Loader animado con los puntos del logo (public/logo.svg): el logo se
// muestra quieto medio segundo, luego cada punto viaja a un lugar distinto
// de un círculo (mismo tamaño final), el círculo gira un rato, y los puntos
// vuelven a su posición original de logo — y el ciclo se repite mientras el
// loader esté visible. Cada punto viaja al punto del círculo más cercano a
// su posición original para que el recorrido se vea natural.
//
// Implementado con @keyframes de CSS animando cx/cy/r directamente (no SMIL):
// es la forma más confiable/depurable de animar geometría SVG entre
// navegadores — se ve en devtools como cualquier otra animación CSS.
const EASE = "cubic-bezier(0.34, 1.2, 0.64, 1)";
const START_DELAY_S = 0.5; // pausa mostrando el logo quieto antes de empezar a moverse
const FORM_DUR_S = 0.5;
const HOLD_DUR_S = 3;
const CYCLE_DUR_S = START_DELAY_S + FORM_DUR_S + HOLD_DUR_S + FORM_DUR_S;
const P_START = (START_DELAY_S / CYCLE_DUR_S) * 100;
const P_FORMED = ((START_DELAY_S + FORM_DUR_S) / CYCLE_DUR_S) * 100;
const P_UNFORM_START = 100 - (FORM_DUR_S / CYCLE_DUR_S) * 100;

interface Dot {
  fill: string;
  from: { cx: number; cy: number; r: number };
  to: { cx: number; cy: number; r: number };
}

// Círculo destino: radio 17 alrededor de (23,23), cabe dentro del viewBox
// 46x46 con margen para el tamaño de los puntos (r=4.5).
const DOTS: Dot[] = [
  { fill: "#FF6B4D", from: { cx: 12, cy: 12, r: 5.5 }, to: { cx: 8.28, cy: 14.5, r: 4.5 } },
  { fill: "#2F6BFF", from: { cx: 31, cy: 9, r: 4 }, to: { cx: 23, cy: 6, r: 4.5 } },
  { fill: "#F4B740", from: { cx: 37, cy: 22, r: 5 }, to: { cx: 37.72, cy: 14.5, r: 4.5 } },
  { fill: "#1FB57A", from: { cx: 22, cy: 24, r: 6.5 }, to: { cx: 23, cy: 40, r: 4.5 } },
  { fill: "#2F6BFF", from: { cx: 10, cy: 30, r: 4 }, to: { cx: 8.28, cy: 31.5, r: 4.5 } },
  { fill: "#FF6B4D", from: { cx: 30, cy: 35, r: 5 }, to: { cx: 37.72, cy: 31.5, r: 4.5 } },
];

function buildCss(): string {
  const dotRules = DOTS.map(
    (d, i) => `
      @keyframes cv-dot-${i} {
        0% { cx: ${d.from.cx}px; cy: ${d.from.cy}px; r: ${d.from.r}px; }
        ${P_START}% { cx: ${d.from.cx}px; cy: ${d.from.cy}px; r: ${d.from.r}px; }
        ${P_FORMED}% { cx: ${d.to.cx}px; cy: ${d.to.cy}px; r: ${d.to.r}px; }
        ${P_UNFORM_START}% { cx: ${d.to.cx}px; cy: ${d.to.cy}px; r: ${d.to.r}px; }
        100% { cx: ${d.from.cx}px; cy: ${d.from.cy}px; r: ${d.from.r}px; }
      }
      .cv-dot-${i} { animation: cv-dot-${i} ${CYCLE_DUR_S}s ${EASE} infinite; }
    `,
  ).join("\n");

  const spinRule = `
    @keyframes cv-loader-spin {
      0% { transform: rotate(0deg); }
      ${P_FORMED}% { transform: rotate(0deg); }
      ${P_UNFORM_START}% { transform: rotate(720deg); }
      100% { transform: rotate(720deg); }
    }
    .cv-loader-group { animation: cv-loader-spin ${CYCLE_DUR_S}s linear infinite; }
  `;

  return `${dotRules}\n${spinRule}\n.cv-loader-group { transform-box: fill-box; transform-origin: center; }`;
}

export function BrandLoader({ size = 56 }: { size?: number }) {
  return (
    <svg viewBox="0 0 46 46" width={size} height={size} aria-hidden="true">
      <style>{buildCss()}</style>
      <g className="cv-loader-group">
        {DOTS.map((dot, i) => (
          <circle
            key={i}
            className={`cv-dot-${i}`}
            cx={dot.from.cx}
            cy={dot.from.cy}
            r={dot.from.r}
            fill={dot.fill}
          />
        ))}
      </g>
    </svg>
  );
}
